import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AdvisorApprovalTarget {
  toolCallId: string;
  sessionOrigin: string;
}

/**
 * Global UI shell state.
 *
 * Persisted (localStorage): sidebar collapse and AI-advisor drawer preference —
 * these are durable user preferences. Ephemeral (not persisted): command
 * palette and mobile navigation visibility, which are transient interactions.
 */
interface UiState {
  // Persisted preferences
  sidebarCollapsed: boolean;
  advisorOpen: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleAdvisor: () => void;
  setAdvisorOpen: (open: boolean) => void;

  // Ephemeral interaction state
  advisorApprovalTarget: AdvisorApprovalTarget | null;
  openAdvisorApproval: (target: AdvisorApprovalTarget) => void;
  clearAdvisorApprovalTarget: () => void;
  commandPaletteOpen: boolean;
  setCommandPaletteOpen: (open: boolean) => void;
  toggleCommandPalette: () => void;
  mobileNavOpen: boolean;
  setMobileNavOpen: (open: boolean) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      advisorOpen: false,
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      toggleAdvisor: () =>
        set((state) => ({ advisorOpen: !state.advisorOpen, advisorApprovalTarget: null })),
      setAdvisorOpen: (open) => set({ advisorOpen: open, advisorApprovalTarget: null }),

      advisorApprovalTarget: null,
      openAdvisorApproval: (target) => set({ advisorOpen: true, advisorApprovalTarget: target }),
      clearAdvisorApprovalTarget: () => set({ advisorApprovalTarget: null }),
      commandPaletteOpen: false,
      setCommandPaletteOpen: (open) => set({ commandPaletteOpen: open }),
      toggleCommandPalette: () =>
        set((state) => ({ commandPaletteOpen: !state.commandPaletteOpen })),
      mobileNavOpen: false,
      setMobileNavOpen: (open) => set({ mobileNavOpen: open }),
    }),
    {
      name: 'helios.ui',
      // Only persist durable preferences, never transient overlay state.
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        advisorOpen: state.advisorOpen,
      }),
    },
  ),
);
